import type { ApiWire } from '@/domain/shared/types';
import type {
    ConnectionValueDto,
    CredentialStatusDto,
    LocationProviderConfigDto,
    ProviderFieldSchemaDto,
    ProviderGroupDto,
    ProviderRowDto,
    ProviderSchemaDto,
    StoredExtraDto,
    UnreadableValueDto,
} from '@/services/types';

import {
    credentialFlags,
    dateOrNull,
    fieldPatches,
    fieldValueFromDto,
    fieldValuesFromDto,
    isFieldKind,
    isSecretName,
    secretsPatchToDto,
} from './fieldValues';
import type { SaveProviderConfigRequestWire } from './secretRequests';
import type {
    CredentialState,
    CredentialStatus,
    FieldValue,
    LocationProviderConfig,
    ProviderFieldSchema,
    ProviderGroup,
    ProviderRow,
    ProviderSchema,
    SaveProviderConfigInput,
    SecretName,
    SecretsPatch,
    StoredExtra,
    UnreadableValue,
} from './types';

/** A field of a kind this build does not know is left out rather than edited as text. */
export function mapFieldSchemas(
    dtos: readonly ApiWire<ProviderFieldSchemaDto>[]
): ProviderFieldSchema[] {
    return dtos.flatMap((dto): ProviderFieldSchema[] => {
        const kind: string = dto.kind;
        if (!isFieldKind(kind)) return [];
        return [
            {
                key: dto.key,
                scope: dto.scope === 'site' ? 'site' : 'pen',
                kind,
                required: dto.required,
                enumValues: dto.enumValues ?? [],
                labelKey: dto.labelKey,
                helpKey: dto.helpKey ?? null,
                subject: dto.subject ?? null,
                advanced: dto.advanced,
                max: dto.max ?? null,
                defaultValue: dto.defaultValue ?? null,
                display: dto.display === 'machine' ? 'machine' : null,
            },
        ];
    });
}

export function mapSchemas(dtos: readonly ApiWire<ProviderSchemaDto>[]): ProviderSchema[] {
    return dtos.map((dto) => ({
        providerName: dto.providerName,
        authType: dto.authType,
        fields: mapFieldSchemas(dto.fields),
    }));
}

function mapConnection(dtos: readonly ApiWire<ConnectionValueDto>[]): ProviderGroup['connection'] {
    return Object.fromEntries(
        dtos.map((dto) => [
            dto.key,
            {
                value: fieldValueFromDto(dto),
                varies: dto.varies,
                perPen: Object.fromEntries(
                    dto.perPen.map((pen) => [pen.cageId, fieldValueFromDto(pen)])
                ),
            },
        ])
    );
}

function credentialState(value: string): CredentialState {
    return value === 'set' || value === 'varies' ? value : 'notSet';
}

function mapCredentialStatuses(
    dtos: readonly ApiWire<CredentialStatusDto>[]
): Partial<Record<SecretName, CredentialStatus>> {
    const statuses: Partial<Record<SecretName, CredentialStatus>> = {};
    for (const dto of dtos) {
        if (!isSecretName(dto.name)) continue;
        statuses[dto.name] = {
            state: credentialState(dto.state),
            changedAt: dateOrNull(dto.changedAt),
        };
    }
    return statuses;
}

/** The generated types drop `nullable`, so an absent value and null both mean withheld. */
export function mapStoredExtra(dto: ApiWire<StoredExtraDto>): StoredExtra {
    return { path: dto.path, value: dto.value ?? null, hidden: dto.hidden };
}

export function mapUnreadable(dto: ApiWire<UnreadableValueDto>): UnreadableValue {
    return { key: dto.key, type: dto.type, value: dto.value ?? null, blocked: dto.blocked };
}

function mapRow(dto: ApiWire<ProviderRowDto>): ProviderRow {
    return {
        rowId: dto.rowId,
        cageId: dto.cageId,
        isActive: dto.isActive,
        updatedAt: dto.updatedAt,
        fields: fieldValuesFromDto(dto.fields),
        credentials: credentialFlags(dto.credentials),
        health: {
            lastFetchedAt: dateOrNull(dto.health.lastFetchedAt),
            daysWithData: dto.health.daysWithData,
            hasData: dto.health.hasData,
        },
        description: dto.description ?? null,
        storedExtras: dto.storedExtras.map(mapStoredExtra),
        unreadable: dto.unreadable.map(mapUnreadable),
    };
}

function mapGroup(dto: ApiWire<ProviderGroupDto>): ProviderGroup {
    return {
        providerName: dto.providerName,
        authType: dto.authType,
        connection: mapConnection(dto.connection),
        credentials: mapCredentialStatuses(dto.credentials),
        rows: dto.rows.map(mapRow),
    };
}

export function mapLocationConfig(dto: ApiWire<LocationProviderConfigDto>): LocationProviderConfig {
    return {
        locationId: dto.locationId,
        clientId: dto.clientId,
        pens: dto.pens.map((pen) => ({ cageId: pen.cageId, name: pen.name })),
        providers: dto.providers.map(mapGroup),
        unsupported: dto.unsupported.map((entry) => ({
            providerName: entry.providerName,
            cageIds: [...entry.cageIds],
        })),
    };
}

/** Site-level parts go out only when present, so an untouched part stays as stored. */
export function siteParts(input: {
    connection: Record<string, FieldValue>;
    secrets: SecretsPatch;
}): Pick<SaveProviderConfigRequestWire, 'connection' | 'secrets'> {
    const connection = fieldPatches(input.connection);
    const secrets = secretsPatchToDto(input.secrets);
    return {
        ...(connection.length > 0 && { connection }),
        ...(secrets && { secrets }),
    };
}

export function toSaveRequest(input: SaveProviderConfigInput): SaveProviderConfigRequestWire {
    return {
        ...siteParts(input),
        pens: input.pens.map((pen) => {
            const secrets = secretsPatchToDto(pen.secrets);
            return {
                cageId: pen.cageId,
                // Left out for a new row: the server reads a missing id and instant as null.
                ...(pen.rowId !== null && { rowId: pen.rowId }),
                ...(pen.expectedUpdatedAt !== null && {
                    expectedUpdatedAt: pen.expectedUpdatedAt,
                }),
                ...(pen.isActive !== undefined && { isActive: pen.isActive }),
                ...(pen.remove !== undefined && { remove: pen.remove }),
                fields: fieldPatches(pen.fields),
                ...(secrets && { secrets }),
            };
        }),
        ...(input.testToken !== undefined && { testToken: input.testToken }),
        ...(input.testOverride !== undefined && { testOverride: input.testOverride }),
    };
}
